/**
 * services/toast.ts · 右下角浮动 toast · 替代 alert()
 *
 * 复用 v200 tnToast 设计:
 *   - 4 类型 (success / info / warning / error)
 *   - 自动 3.5s 隐 (或手动关)
 *   - fade-in/out 平滑
 *   - 多 toast 堆叠
 *
 * 用法:
 *   import { toast } from '@services/toast';
 *   toast.success('✅ 报价已保存');
 *   toast.error('❌ 客户不存在');
 *   toast.warn('⚠️ 库存不足');
 *   toast.info('ℹ️ 已同步 5 笔', { duration: 5000 });
 */

type ToastType = 'success' | 'info' | 'warning' | 'error';

interface ToastOpts {
  duration?: number;  // ms · default 3500
  persistent?: boolean;  // true = 不自动关
  cta?: { label: string; hash: string };  // v353 · "下一步" CTA button (replaces v200 v172)
}

/**
 * v353 · 关键字命中自动注入 CTA · 复用 v200 v172 TN_V172_FLOW_CTA
 *
 * 命中规则 (按 message 关键字):
 *   - "报价" + "已提交/已保存" → CTA "👉 转 SO" → #salesOrderCreate
 *   - "订单" + "已确认/已保存" → CTA "👉 生成工单" → #woCreate
 *   - "工单" + "已创建/已派工" → CTA "👉 看 Gantt" → #ganttSchedule
 *   - "收货" + "完成/已收" → CTA "👉 通知 IQC" → #iqcIncoming
 *   - "发货" + "完成/已发" → CTA "👉 开发票" → #arInvoiceCreate
 *   - "收款" + "完成/已收" → CTA "👉 看账龄" → #arAging
 *   - "QC" + "合格" → CTA "👉 入库" → #receiveMaterial (raw) / #finishedProductList (finished)
 */
const CTA_RULES: Array<{ match: RegExp; label: string; hash: string }> = [
  { match: /(报价|quotation|báo giá).*?(提交|保存|sav|nộp)/i, label: '👉 转 SO', hash: 'salesOrderCreate' },
  { match: /(订单|sales.?order|đơn hàng).*?(确认|创建|sav|x[áa]c nhận)/i, label: '👉 生成工单', hash: 'woCreate' },
  { match: /(工单|wo|lệnh.*?sản xuất).*?(创建|派工|sav|tạo)/i, label: '👉 看 Gantt', hash: 'ganttSchedule' },
  { match: /(收货|received|nhận hàng).*?(完成|完|đã|xong)/i, label: '👉 通知 IQC', hash: 'iqcIncoming' },
  { match: /(发货|shipped|giao hàng).*?(完成|完|xong|đã)/i, label: '👉 开发票', hash: 'arInvoiceCreate' },
  { match: /(收款|payment|thu tiền).*?(完成|已|xong)/i, label: '👉 看账龄', hash: 'arAging' },
  { match: /(QC|检验|kiểm).*?(合格|pass|đạt)/i, label: '👉 入库', hash: 'finishedProductList' },
  { match: /(客户|customer|khách hàng).*?(创建|新建|sav|tạo)/i, label: '👉 新建报价', hash: 'quotationCreate' },
  { match: /(产品|product|sản phẩm).*?(创建|新建|sav|tạo)/i, label: '👉 维护 BOM', hash: 'bomTreeEditor' },
  { match: /(PR|采购请求|đề xuất).*?(创建|提交|sav|tạo)/i, label: '👉 转 PO', hash: 'poCreate' },
  { match: /(PO|采购订单|đơn mua).*?(创建|提交|sav|tạo)/i, label: '👉 等收货', hash: 'receiveMaterial' },
];

function autoDetectCta(message: string): { label: string; hash: string } | null {
  for (const rule of CTA_RULES) {
    if (rule.match.test(message)) return { label: rule.label, hash: rule.hash };
  }
  return null;
}

const COLORS: Record<ToastType, { bg: string; icon: string }> = {
  success: { bg: '#10b981', icon: '✅' },
  info:    { bg: '#3b82f6', icon: 'ℹ️' },
  warning: { bg: '#f59e0b', icon: '⚠️' },
  error:   { bg: '#ef4444', icon: '❌' },
};

const CONTAINER_ID = 'tn-toast-container';
const DEFAULT_DURATION = 3500;

function ensureContainer(): HTMLElement {
  let c = document.getElementById(CONTAINER_ID);
  if (!c) {
    c = document.createElement('div');
    c.id = CONTAINER_ID;
    c.style.cssText = `
      position: fixed;
      bottom: 16px;
      right: 16px;
      z-index: 999999;
      display: flex;
      flex-direction: column-reverse;
      gap: 8px;
      pointer-events: none;
    `;
    document.body.appendChild(c);
    injectCss();
  }
  return c;
}

function injectCss(): void {
  if (document.getElementById('tn-toast-css')) return;
  const style = document.createElement('style');
  style.id = 'tn-toast-css';
  style.textContent = `
    @keyframes tnToastIn {
      from { opacity: 0; transform: translateX(120%); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes tnToastOut {
      from { opacity: 1; transform: translateX(0); }
      to   { opacity: 0; transform: translateX(120%); }
    }
    .tn-toast {
      pointer-events: auto;
      min-width: 280px;
      max-width: 420px;
      padding: 10px 14px;
      border-radius: 8px;
      color: #fff;
      font-size: 14px;
      line-height: 1.4;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
      display: flex;
      align-items: flex-start;
      gap: 8px;
      animation: tnToastIn 0.2s ease-out;
    }
    .tn-toast.tn-toast--out { animation: tnToastOut 0.2s ease-in forwards; }
    .tn-toast__icon { font-size: 16px; flex-shrink: 0; }
    .tn-toast__msg  { flex: 1; }
    .tn-toast__close {
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      font-size: 16px;
      line-height: 1;
      padding: 0 2px;
    }
    .tn-toast__close:hover { color: #fff; }
    .tn-toast__cta {
      background: rgba(255, 255, 255, 0.22);
      border: 1px solid rgba(255, 255, 255, 0.4);
      color: #fff;
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;
      padding: 3px 10px;
      border-radius: 4px;
      margin-left: 6px;
      transition: background 0.15s;
    }
    .tn-toast__cta:hover { background: rgba(255, 255, 255, 0.34); }
  `;
  document.head.appendChild(style);
}

function show(type: ToastType, message: string, opts: ToastOpts = {}): void {
  const container = ensureContainer();
  const { bg, icon } = COLORS[type];

  // v353 · 自动 CTA 检测 (success 类型默认开 · 其他显式传)
  let cta = opts.cta;
  if (!cta && type === 'success') {
    cta = autoDetectCta(message) || undefined;
  }

  const el = document.createElement('div');
  el.className = 'tn-toast';
  el.style.background = bg;
  const ctaHtml = cta
    ? `<button class="tn-toast__cta" data-hash="${escapeHtml(cta.hash)}">${escapeHtml(cta.label)}</button>`
    : '';
  el.innerHTML = `
    <span class="tn-toast__icon">${icon}</span>
    <span class="tn-toast__msg">${escapeHtml(message)}</span>
    ${ctaHtml}
    <button class="tn-toast__close" aria-label="close">×</button>
  `;
  container.appendChild(el);

  const close = () => {
    el.classList.add('tn-toast--out');
    setTimeout(() => el.remove(), 220);
  };

  el.querySelector('.tn-toast__close')!.addEventListener('click', close);

  // CTA click 跳页 + 自动关 toast
  if (cta) {
    el.querySelector<HTMLButtonElement>('.tn-toast__cta')!.addEventListener('click', () => {
      window.location.hash = `#${cta!.hash}`;
      close();
    });
  }

  // 含 CTA 的 toast 默认延长到 6s (给用户点击时间)
  const duration = opts.duration || (cta ? 6000 : DEFAULT_DURATION);
  if (!opts.persistent) {
    setTimeout(close, duration);
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}

export const toast = {
  success: (msg: string, opts?: ToastOpts) => show('success', msg, opts),
  info:    (msg: string, opts?: ToastOpts) => show('info', msg, opts),
  warn:    (msg: string, opts?: ToastOpts) => show('warning', msg, opts),
  warning: (msg: string, opts?: ToastOpts) => show('warning', msg, opts),
  error:   (msg: string, opts?: ToastOpts) => show('error', msg, opts),
};
